// thank-you-page.tsx
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { Card, Typography, Button, Space, Flex, Tag } from 'antd';
import { useStore } from '../../helpers/use-store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircleOutlined,
  RocketOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import Confetti from 'react-confetti';
import { BillingCycle } from '@monorepo/types';
import { usePollUserState } from '../../hooks/use-poll-user-state';
import { useTranslation } from 'react-i18next';

import './thank-you-page.scss';

const { Title, Text, Paragraph } = Typography;

const ThankYouPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    dataStore: { userStore, planStore, seriesStore },
  } = useStore();

  const [showConfetti, setShowConfetti] = React.useState(true);
  const { startPolling } = usePollUserState();

  useEffect(() => {
    // Fetch latest data
    seriesStore.fetchAll();
    userStore.getLatestUser();

    // The provider webhook (subscription/topup activation) is asynchronous,
    // so the page often paints with stale user state — poll until it lands.
    startPolling();

    // Stop confetti after 5 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const hasSeries = seriesStore.items.length > 0;

  // Get purchase info from query params
  const isTopup = searchParams.get('isTopup') === 'true';
  const planName = searchParams.get('planName') || '';
  const credits = searchParams.get('credits') || '0';
  const billingCycle = searchParams.get('billingCycle') || BillingCycle.Monthly;

  // Fallback to user's current plan if no query params
  const plan = planStore.items.find(
    (p) => p._id === userStore.userSubscription.planId
  );
  const displayPlanName = planName || plan?.name || 'Premium';

  return (
    <div className="thank-you-page">
      <Helmet>
        <title>Thank You! | ShortsNinja</title>
        <meta name="description" content="Welcome to ShortsNinja Premium!" />
      </Helmet>

      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <Card className="thank-you-card">
        {/* Success Icon */}
        <div className="success-icon">
          <CheckCircleOutlined />
        </div>

        {/* Main Message */}
        <Title level={1} style={{ margin: '24px 0 16px 0', fontSize: '36px' }}>
          {isTopup ? 'Credits Added! 🎉' : 'Welcome to ShortsNinja! 🎉'}
        </Title>

        <Paragraph
          style={{ fontSize: '18px', color: '#666', marginBottom: '32px' }}
        >
          {isTopup
            ? `You've successfully purchased ${credits} topup credits that never expire!`
            : `Your subscription is now active and you're ready to create amazing content!`}
        </Paragraph>

        {/* Purchase Info */}
        <div className="subscription-badge">
          <Tag
            icon={isTopup ? <ThunderboltOutlined /> : <CrownOutlined />}
            color={isTopup ? 'magenta' : 'gold'}
            style={{
              fontSize: '16px',
              padding: '8px 16px',
              borderRadius: '20px',
            }}
          >
            {isTopup
              ? `${displayPlanName} - ${credits} Credits`
              : `${displayPlanName} Plan Active`}
          </Tag>
        </div>

        {/* What's Next Section */}
        <div className="next-steps">
          <Title level={3} style={{ marginBottom: '24px' }}>
            What's Next?
          </Title>

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* For Topup: Primary action is creating videos */}
            {isTopup ? (
              <>
                {/* Create Video - Primary CTA for topup */}
                <Card
                  className="action-card"
                  hoverable
                  onClick={() => navigate('/videos/form/new')}
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap={16}>
                      <div className="action-icon videos">
                        <VideoCameraOutlined />
                      </div>
                      <div>
                        <Text
                          strong
                          style={{ fontSize: '16px', display: 'block' }}
                        >
                          Create a Video
                        </Text>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          Start creating AI-powered videos with your credits
                        </Text>
                      </div>
                    </Flex>
                    <ArrowRightOutlined
                      style={{ fontSize: '20px', color: '#722ed1' }}
                    />
                  </Flex>
                </Card>

                {/* Manage Series - Only if user has active subscription AND series */}
                {hasSeries && userStore.isSubscriptionActive && (
                  <Card
                    className="action-card"
                    hoverable
                    onClick={() => navigate('/series')}
                  >
                    <Flex align="center" justify="space-between">
                      <Flex align="center" gap={16}>
                        <div className="action-icon series">
                          <RocketOutlined />
                        </div>
                        <div>
                          <Text
                            strong
                            style={{ fontSize: '16px', display: 'block' }}
                          >
                            Manage Your Series
                          </Text>
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            You have {seriesStore.items.length} series ready
                          </Text>
                        </div>
                      </Flex>
                      <ArrowRightOutlined
                        style={{ fontSize: '20px', color: '#1890ff' }}
                      />
                    </Flex>
                  </Card>
                )}
              </>
            ) : (
              <>
                {/* For Subscription: Show series first if they have them */}
                {hasSeries && (
                  <Card
                    className="action-card"
                    hoverable
                    onClick={() => navigate('/series')}
                  >
                    <Flex align="center" justify="space-between">
                      <Flex align="center" gap={16}>
                        <div className="action-icon series">
                          <RocketOutlined />
                        </div>
                        <div>
                          <Text
                            strong
                            style={{ fontSize: '16px', display: 'block' }}
                          >
                            Manage Your Series
                          </Text>
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            You have {seriesStore.items.length} series ready to
                            create videos
                          </Text>
                        </div>
                      </Flex>
                      <ArrowRightOutlined
                        style={{ fontSize: '20px', color: '#1890ff' }}
                      />
                    </Flex>
                  </Card>
                )}

                {/* Create Video */}
                <Card
                  className="action-card"
                  hoverable
                  onClick={() => navigate('/videos/form/new')}
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap={16}>
                      <div className="action-icon videos">
                        <VideoCameraOutlined />
                      </div>
                      <div>
                        <Text
                          strong
                          style={{ fontSize: '16px', display: 'block' }}
                        >
                          Create Your First Video
                        </Text>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          Start creating AI-powered videos instantly
                        </Text>
                      </div>
                    </Flex>
                    <ArrowRightOutlined
                      style={{ fontSize: '20px', color: '#722ed1' }}
                    />
                  </Flex>
                </Card>

                {/* Create Series - Only for new subscriptions without series */}
                {!hasSeries && (
                  <Card
                    className="action-card"
                    hoverable
                    onClick={() => navigate('/series/form/new')}
                  >
                    <Flex align="center" justify="space-between">
                      <Flex align="center" gap={16}>
                        <div className="action-icon series">
                          <RocketOutlined />
                        </div>
                        <div>
                          <Text
                            strong
                            style={{ fontSize: '16px', display: 'block' }}
                          >
                            Create a Series
                          </Text>
                          <Text type="secondary" style={{ fontSize: '14px' }}>
                            Automate video creation with custom series
                          </Text>
                        </div>
                      </Flex>
                      <ArrowRightOutlined
                        style={{ fontSize: '20px', color: '#1890ff' }}
                      />
                    </Flex>
                  </Card>
                )}
              </>
            )}
          </Space>
        </div>

        {/* Features Unlocked */}
        <div className="features-unlocked">
          <Title level={4} style={{ marginBottom: '16px' }}>
            ✨ Features Unlocked
          </Title>
          <div className="features-grid">
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>AI-generated content</Text>
            </div>
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>Auto-publish to social media</Text>
            </div>
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>HD video quality</Text>
            </div>
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>Multiple AI voices</Text>
            </div>
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>Background music</Text>
            </div>
            <div className="feature-item">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>Priority support</Text>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="support-section">
          <Text type="secondary">
            Need help getting started?{' '}
            <a
              target={'_blank'}
              href="https://help.shortsninja.com/"
              style={{ fontWeight: 600 }}
              rel="noreferrer"
            >
              Visit our Help Center
            </a>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default observer(ThankYouPage);
